import { formatCurrencyID, formatQuantity } from '../../utils/currency';
import ItemAutocomplete from '../ItemAutocomplete';
import { SearchableSelect } from '../SearchableSelect';
import { SignedAmountInput } from '../SignedAmountInput';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { Calendar } from '../ui/calendar';

export function POForm({ biayaAngkut, companies, formData, handleAddAddress, handleAddItem, handleAddressChange, handleCreate, handleEdit, handleItemChange, handleItemSelectProduct, handleRemoveAddress, handleRemoveItem, handleSupplierChange, isEdit, pembulatan, ppnAmount, selectedPO, setFormData, subtotal, suppliers, totalAmount }) {
  return (
    <form onSubmit={isEdit ? handleEdit : handleCreate} className="space-y-4">
      {/* PO Date - Auto-filled */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tanggal PO</Label>
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-slate-50">
            <CalendarIcon className="h-4 w-4 text-slate-500" />
            <span className="text-sm">{new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <p className="text-xs text-slate-500">Otomatis terisi tanggal hari ini</p>
        </div>
        <div className="space-y-2">
          <Label>Nomor PO</Label>
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-slate-50">
            <span className="text-sm font-mono">{isEdit && selectedPO ? selectedPO.po_number : 'Auto-generated'}</span>
          </div>
          <p className="text-xs text-slate-500">{isEdit ? 'Nomor PO tidak dapat diubah' : 'Nomor PO akan di-generate otomatis'}</p>
        </div>
      </div>

      {/* Supplier Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Supplier *</Label>
          <SearchableSelect
            options={suppliers}
            value={formData.supplier_id}
            onChange={handleSupplierChange}
            displayKey="nama_supplier"
            valueKey="id"
            placeholder="Ketik nama supplier..."
            testId="supplier-select"
          />
        </div>
        <div className="space-y-2">
          <Label>Email Supplier (untuk kirim PO)</Label>
          <Input
            type="email"
            value={formData.email_supplier}
            onChange={(e) => setFormData({ ...formData, email_supplier: e.target.value.toLowerCase() })}
            placeholder="email@supplier.com"
            style={{ textTransform: 'lowercase' }}
          />
          <p className="text-xs text-slate-500">Email tujuan untuk pengiriman PO otomatis</p>
        </div>
      </div>

      {/* WhatsApp Number */}
      <div className="space-y-2">
        <Label>Nomor WhatsApp Supplier</Label>
        <Input
          type="tel"
          value={formData.nomor_wa_supplier}
          onChange={(e) => setFormData({ ...formData, nomor_wa_supplier: e.target.value.replace(/[^0-9]/g, '') })}
          placeholder="6281234567890"
        />
        <p className="text-xs text-slate-500">Format: 62 + nomor (untuk kirim pesan WA nantinya)</p>
      </div>

      {/* Company (Ditujukan untuk) Selection */}
      <div className="space-y-2">
        <Label>Ditujukan untuk (Perusahaan) *</Label>
        <SearchableSelect
          options={companies}
          value={formData.company_id}
          onChange={(value) => setFormData({ ...formData, company_id: value })}
          displayKey="nama_perusahaan"
          valueKey="id"
          placeholder="Ketik nama perusahaan..."
          testId="company-select"
        />
      </div>

      {/* Alamat Pengiriman - Multiple */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Alamat Pengiriman *</Label>
          <Button type="button" size="sm" variant="outline" onClick={handleAddAddress}>
            <Plus className="h-4 w-4 mr-1" /> Tambah Alamat
          </Button>
        </div>
        {formData.alamat_pengiriman.map((address, index) => (
          <div key={index} className="flex gap-2">
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
              value={address}
              onChange={(e) => handleAddressChange(index, e.target.value)}
              placeholder={`ALAMAT PENGIRIMAN ${index + 1}`}
              required={index === 0}
            />
            {formData.alamat_pengiriman.length > 1 && (
              <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveAddress(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Termin & Jenis Pengiriman */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Termin Pembayaran *</Label>
          <Select 
            value={formData.termin_pembayaran.toString()} 
            onValueChange={(value) => setFormData({ ...formData, termin_pembayaran: parseInt(value) })}
          >
            <SelectTrigger data-testid="termin-select">
              <SelectValue placeholder="Pilih Termin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Hari</SelectItem>
              <SelectItem value="14">14 Hari</SelectItem>
              <SelectItem value="20">20 Hari</SelectItem>
              <SelectItem value="30">30 Hari</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Jenis Pengiriman *</Label>
          <Select 
            value={formData.jenis_pengiriman} 
            onValueChange={(value) => setFormData({ ...formData, jenis_pengiriman: value })}
          >
            <SelectTrigger data-testid="delivery-type-select">
              <SelectValue placeholder="Pilih Jenis Pengiriman" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full (Sekaligus)</SelectItem>
              <SelectItem value="partial">Partial (Bertahap)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Items *</Label>
          <Button type="button" size="sm" onClick={handleAddItem} data-testid="add-item-button">
            <Plus className="h-4 w-4 mr-1" /> Tambah Item
          </Button>
        </div>
        <div className="border rounded-lg p-3 space-y-3">
          {formData.items.map((item, index) => (
            <div key={index} className="space-y-2 pb-3 border-b last:border-b-0 last:pb-0">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Label className="text-xs">Nama Item</Label>
                  <ItemAutocomplete
                    value={item.name}
                    onChange={(val) => handleItemChange(index, 'name', val)}
                    onSelect={(product) => handleItemSelectProduct(index, product)}
                    placeholder="CARI NAMA ITEM DARI DAFTAR BARANG"
                  />
                  {item.product_id ? (
                    <p className="text-xs text-emerald-600 mt-1" data-testid={`item-catalog-ok-${index}`}>
                      {item.kode_barang ? `${item.kode_barang} — ` : ''}dari Daftar Barang
                    </p>
                  ) : !item.catalog_exempt && item.name ? (
                    <p className="text-xs text-amber-600 mt-1" data-testid={`item-catalog-warn-${index}`}>
                      Belum dipilih dari Daftar Barang — pilih dari saran yang muncul
                    </p>
                  ) : null}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Satuan</Label>
                  <Select 
                    value={item.satuan || 'pcs'} 
                    onValueChange={(value) => handleItemChange(index, 'satuan', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Satuan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Pcs</SelectItem>
                      <SelectItem value="unit">Unit</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="pack">Pack</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="lusin">Lusin</SelectItem>
                      <SelectItem value="kg">Kg</SelectItem>
                      <SelectItem value="gram">Gram</SelectItem>
                      <SelectItem value="liter">Liter</SelectItem>
                      <SelectItem value="meter">Meter</SelectItem>
                      <SelectItem value="roll">Roll</SelectItem>
                      <SelectItem value="zak">Zak</SelectItem>
                      <SelectItem value="ekor">Ekor</SelectItem>
                      <SelectItem value="karton">Karton</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Qty</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={item.quantity_display ?? formatQuantity(item.quantity)}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Harga (Rp)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={item.unit_price_display ?? formatCurrencyID(item.unit_price)}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="col-span-1">
                  {formData.items.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              {formData.jenis_pengiriman === 'partial' && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Tanggal Kirim:</Label>
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[200px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {item.delivery_date ? format(new Date(item.delivery_date), 'dd MMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={item.delivery_date ? new Date(item.delivery_date) : undefined}
                        onSelect={(date) => handleItemChange(index, 'delivery_date', date ? format(date, 'yyyy-MM-dd') : '')}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Virtual Account Option */}
      <div className="space-y-3 border rounded-lg p-4 bg-amber-50">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id={isEdit ? "edit_uses_va" : "uses_va"}
            checked={formData.uses_va}
            onCheckedChange={(checked) => setFormData({ ...formData, uses_va: checked })}
            data-testid="va-checkbox"
          />
          <Label htmlFor={isEdit ? "edit_uses_va" : "uses_va"} className="cursor-pointer text-amber-800">
            Supplier menggunakan <strong>Virtual Account</strong>
          </Label>
        </div>
        {formData.uses_va && formData.supplier_id && formData.company_id && (
          <p className="text-xs text-amber-600 pl-6">Nomor rekening akan diambil dari Data VA berdasarkan Supplier + Company yang dipilih</p>
        )}
      </div>

      {/* PPn Option */}
      <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id={isEdit ? "edit_has_ppn" : "has_ppn"}
            checked={formData.has_ppn}
            onCheckedChange={(checked) => setFormData({ ...formData, has_ppn: checked })}
            data-testid="ppn-checkbox"
          />
          <Label htmlFor={isEdit ? "edit_has_ppn" : "has_ppn"} className="cursor-pointer">Tambahkan PPn (Pajak Pertambahan Nilai)</Label>
        </div>
        {formData.has_ppn && (
          <div className="space-y-2 pl-6">
            <Label htmlFor={isEdit ? "edit_ppn_percentage" : "ppn_percentage"}>Persentase PPn (%)</Label>
            <Input
              id={isEdit ? "edit_ppn_percentage" : "ppn_percentage"}
              type="number"
              value={formData.ppn_percentage}
              onChange={(e) => setFormData({ ...formData, ppn_percentage: parseFloat(e.target.value) || 0 })}
              min="0"
              max="100"
              step="0.1"
              className="w-32"
              data-testid="ppn-percentage-input"
            />
          </div>
        )}
      </div>

      {/* Pembulatan Harga */}
      <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
        <Label htmlFor={isEdit ? "edit_pembulatan" : "pembulatan"}>Pembulatan Harga (Rp)</Label>
        <p className="text-xs text-slate-500">Koreksi pada total PO, tidak mempengaruhi DPP maupun perhitungan PPn. Boleh diisi minus.</p>
        <SignedAmountInput
          id={isEdit ? "edit_pembulatan" : "pembulatan"}
          value={formData.pembulatan}
          onChange={(val) => setFormData({ ...formData, pembulatan: val })}
          className="w-48 font-mono"
          data-testid="pembulatan-input"
        />
      </div>

      {/* Calculation Summary */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-2">
        <div className="flex justify-between text-sm">
          <span>Subtotal Item</span>
          <span className="font-mono">Rp {subtotal.toLocaleString('id-ID')}</span>
        </div>
        {formData.has_ppn && (
          <div className="flex justify-between text-sm">
            <span>PPn ({formData.ppn_percentage}%)</span>
            <span className="font-mono">Rp {ppnAmount.toLocaleString('id-ID')}</span>
          </div>
        )}
        {biayaAngkut > 0 && (
          <div className="flex justify-between text-sm">
            <span>Biaya Angkut</span>
            <span className="font-mono">Rp {biayaAngkut.toLocaleString('id-ID')}</span>
          </div>
        )}
        {pembulatan !== 0 && (
          <div className="flex justify-between text-sm">
            <span>Pembulatan</span>
            <span className="font-mono">{pembulatan >= 0 ? '+' : ''}Rp {pembulatan.toLocaleString('id-ID')}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total PO</span>
          <span className="font-mono text-blue-600">Rp {totalAmount.toLocaleString('id-ID')}</span>
        </div>
      </div>

      {/* Biaya Angkut */}
      <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
        <Label htmlFor={isEdit ? "edit_biaya_angkut" : "biaya_angkut"}>Biaya Angkut (Rp)</Label>
        <p className="text-xs text-slate-500">Biaya angkut ditambahkan setelah PPn (tidak masuk hitungan PPn)</p>
        <Input
          id={isEdit ? "edit_biaya_angkut" : "biaya_angkut"}
          type="number"
          value={formData.biaya_angkut}
          onChange={(e) => setFormData({ ...formData, biaya_angkut: parseFloat(e.target.value) || 0 })}
          placeholder="0"
          className="w-48 font-mono"
          data-testid="biaya-angkut-input"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor={isEdit ? "edit_notes" : "notes"}>Catatan (Optional)</Label>
        <textarea
          id={isEdit ? "edit_notes" : "notes"}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value.toUpperCase() })}
          placeholder="TAMBAHKAN CATATAN JIKA DIPERLUKAN..."
        />
      </div>

      <Button type="submit" className="w-full" data-testid={isEdit ? "update-po-button" : "submit-po-button"}>
        {isEdit ? 'Update Purchase Order' : 'Buat Purchase Order'}
      </Button>
    </form>
  );
}
